// AddTenantModal.jsx
import React, { useState } from 'react';
import { FiX } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import toast from 'react-hot-toast';
import api from '../../services/api';
import config from '../../config/api';

const AddTenantModal = ({ isOpen, onClose, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({
        tenantName: '',
        tenantSlug: '',
        firstName: '',
        lastName: '',
        email: '',
        password: ''
    });

    if (!isOpen) return null;

    const handleSubmit = async () => {
        // Simple client-side validation
        const { tenantName, tenantSlug, firstName, lastName, email, password } = form;
        if (!tenantName || !tenantSlug || !firstName || !lastName || !email || !password) {
            toast.error('Please fill all required fields');
            return;
        }

        setLoading(true);
        try {
            const response = await api.post(config.endpoints.auth.registerTenant, form);
            if (response.success) {
                toast.success('Tenant created successfully!');
                onSuccess();
                onClose();
            } else {
                throw new Error(response.message || 'Failed to create tenant');
            }
        } catch (error) {
            console.error('Error creating tenant:', error);
            toast.error(error.message || 'Failed to create tenant');
        } finally {
            setLoading(false);
        }
    };

    const generateSlug = (name) => {
        return name
            .toLowerCase()
            .trim()
            .replace(/[^a-z0-9\s-]/g, '')   // remove special chars
            .replace(/\s+/g, '-')           // spaces → hyphen
            .replace(/-+/g, '-');           // remove duplicate hyphens
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'tenantName') {
            setForm({
                ...form,
                tenantName: value,
                tenantSlug: generateSlug(value),
            });
        } else {
            setForm({ ...form, [name]: value });
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-lg p-6 relative">
                <button
                    onClick={onClose}
                    className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                >
                    <SafeIcon icon={FiX} className="w-5 h-5" />
                </button>

                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Add New Tenant</h2>

                <div className="space-y-4">
                    <input
                        type="text"
                        name="tenantName"
                        placeholder="Tenant Name"
                        value={form.tenantName}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                    <input
                        type="text"
                        name="tenantSlug"
                        placeholder="Tenant Slug"
                        value={form.tenantSlug}
                        onChange={handleChange}
                        readOnly // auto-generated
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white bg-gray-100 dark:bg-gray-600"
                    />

                    <h3 className="font-medium text-gray-900 dark:text-white">Initial User</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <input
                            type="text"
                            name="firstName"
                            placeholder="First Name"
                            value={form.firstName}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        />
                        <input
                            type="text"
                            name="lastName"
                            placeholder="Last Name"
                            value={form.lastName}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        />
                        <input
                            type="email"
                            name="email"
                            placeholder="Email"
                            value={form.email}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        />
                        <input
                            type="password"
                            name="password"
                            placeholder="Password"
                            value={form.password}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        />
                    </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-300 hover:bg-gray-400 rounded-lg dark:bg-gray-700 dark:hover:bg-gray-600"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={loading}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center space-x-2"
                    >
                        {loading && <div className="animate-spin h-4 w-4 border-b-2 border-white rounded-full" />}
                        <span>Create Tenant</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddTenantModal;